import { Navbar } from '../components/Navbar';
import { FileText, Check, Shield, AlertTriangle } from 'lucide-react';

function ContractorTermsPage() {
  return (
    <div>
      <Navbar />
      <div className="min-h-screen bg-slate-50 pt-20 pb-12">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Independent Contractor Agreement</h1>
                <p className="text-slate-500">Hambre Platform - Terms & Conditions</p>
              </div>
            </div>

            <div className="prose prose-slate max-w-none">
              <p className="text-slate-600 mb-6">
                This Independent Contractor Agreement ("Agreement") is entered into between Hambre ("Company") and the individual or entity agreeing to these terms ("Contractor").
              </p>
              <p className="text-sm text-slate-400 mb-6">Last updated: February 2026</p>

              <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-4">1. Independent Contractor Status</h2>
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <ul className="space-y-2 text-slate-600">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>You are an <strong>independent contractor</strong> and NOT an employee of Hambre.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>You have the right to control how, when, and where you perform services.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>You are free to work for other platforms and businesses simultaneously.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>You may accept or decline delivery/sales opportunities at your discretion.</span>
                  </li>
                </ul>
              </div>

              <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-4">2. Tax Responsibilities</h2>
              <p className="text-slate-600 mb-4">
                As an independent contractor, you are solely responsible for:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600 mb-4">
                <li>Reporting all income to HM Revenue & Customs (HMRC)</li>
                <li>Paying all applicable taxes, including income tax and National Insurance contributions</li>
                <li>Maintaining accurate records of your earnings and expenses</li>
                <li>Filing self-assessment tax returns as required by law</li>
                <li>Any VAT registration requirements if applicable</li>
              </ul>

              {/* Driver Verification Requirements - NEW SECTION */}
              <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-500" />
                3. Driver Verification Requirements
              </h2>

              <h3 className="font-semibold text-slate-700 mt-6 mb-3">3.1 Legal Compliance Requirement</h3>
              <p className="text-slate-600 mb-4">
                Hambre requires all delivery partners ("Drivers") to provide valid documentation, including but not limited to 
                a valid driving licence, proof of motor insurance, vehicle registration documentation, and any other documentation 
                required by applicable law, regulatory authorities, or Hambre's insurance providers.
              </p>

              <h3 className="font-semibold text-slate-700 mt-6 mb-3">3.2 Purpose of Collection</h3>
              <p className="text-slate-600 mb-2">Such documentation is collected solely for the purposes of:</p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600 mb-4">
                <li>Verifying legal eligibility to perform delivery services</li>
                <li>Ensuring compliance with applicable laws and regulations</li>
                <li>Meeting Hambre's contractual and insurance obligations</li>
                <li>Protecting platform users and third parties</li>
              </ul>

              <h3 className="font-semibold text-slate-700 mt-6 mb-3">3.3 Verification Process</h3>
              <p className="text-slate-600 mb-4">
                Documentation submitted by Drivers will be reviewed for authenticity, validity, and compliance with applicable requirements.
              </p>

              <h3 className="font-semibold text-slate-700 mt-6 mb-3">3.4 Deletion of Documents After Review</h3>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-green-800 font-medium mb-2">Your Documents Are Safe</p>
                <p className="text-green-700 text-sm mb-2">Once documentation has been reviewed and verified:</p>
                <ul className="space-y-1 text-green-700 text-sm">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Hambre does not retain copies of the original uploaded documents;</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Raw files (including scans, images, or PDFs) are permanently deleted from active systems;</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Only limited verification metadata is retained.</span>
                  </li>
                </ul>
              </div>

              <h3 className="font-semibold text-slate-700 mt-6 mb-3">3.5 Metadata Retained</h3>
              <p className="text-slate-600 mb-2">Following verification, Hambre retains only the following information:</p>
              <div className="bg-slate-50 rounded-lg p-4 mb-4">
                <ul className="space-y-1 text-slate-600 text-sm">
                  <li>• Document type</li>
                  <li>• Confirmation of verification status</li>
                  <li>• Relevant expiry dates</li>
                  <li>• Unique reference numbers (where necessary)</li>
                  <li>• Date of verification</li>
                  <li>• Internal audit reference</li>
                </ul>
              </div>
              <p className="text-slate-500 text-sm mb-4">
                This data is retained strictly for compliance, audit, and risk management purposes.
              </p>

              <h3 className="font-semibold text-slate-700 mt-6 mb-3">3.6 Ongoing Obligation</h3>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-800 font-medium">Important</p>
                    <p className="text-amber-700 text-sm">
                      Drivers are responsible for ensuring that all documentation remains valid and up to date. 
                      Hambre reserves the right to suspend or terminate access to the platform if documentation expires or is invalid.
                    </p>
                  </div>
                </div>
              </div>

              <h3 className="font-semibold text-slate-700 mt-6 mb-3">3.7 Document Retention for Legal Purposes</h3>
              <p className="text-slate-600 mb-4">
                Hambre reserves the right to retain documentation where required by law, court order, regulatory investigation, or insurance claim.
              </p>

              {/* End of Driver Verification Requirements */}

              <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-4">4. Driver-Specific Terms</h2>
              <p className="text-slate-600 mb-4">
                If you are registering as a delivery driver, you additionally agree that:
              </p>
              <div className="bg-orange-50 rounded-lg p-4 mb-4">
                <ul className="space-y-2 text-slate-700">
                  <li><strong>Vehicle Requirements:</strong> You must have access to a suitable vehicle for deliveries.</li>
                  <li><strong>Licensing:</strong> For motor vehicles (cars, scooters), you must hold a valid driving license.</li>
                  <li><strong>Insurance:</strong> You are responsible for maintaining adequate insurance coverage for delivery work. Hambre does not provide insurance coverage for contractors.</li>
                  <li><strong>Vehicle Maintenance:</strong> You are responsible for all vehicle maintenance, fuel, and related costs.</li>
                  <li><strong>Road Safety:</strong> You must comply with all traffic laws and regulations.</li>
                </ul>
              </div>

              <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-4">5. Salesperson-Specific Terms</h2>
              <p className="text-slate-600 mb-4">
                If you are registering as a salesperson, you additionally agree that:
              </p>
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <ul className="space-y-2 text-slate-700">
                  <li><strong>Commission:</strong> You will earn commission on restaurants you onboard for as long as they remain active on the platform.</li>
                  <li><strong>Payment:</strong> Commission rates are set by Hambre and may be adjusted with notice.</li>
                  <li><strong>Conduct:</strong> You must represent Hambre professionally and honestly to potential restaurant partners.</li>
                  <li><strong>No Guarantees:</strong> You are not guaranteed any minimum earnings or number of restaurant sign-ups.</li>
                </ul>
              </div>

              <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-4">6. Payment & Stripe Connect</h2>
              <p className="text-slate-600 mb-4">
                Payments are processed through Stripe Connect:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600 mb-4">
                <li>You must create and verify a Stripe Connect account to receive payments</li>
                <li>Stripe may require identity verification as part of their compliance requirements</li>
                <li>Payments are typically processed within 2-7 business days</li>
                <li>You are responsible for any fees charged by Stripe or your bank</li>
              </ul>

              <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-4">7. Limitation of Liability</h2>
              <p className="text-slate-600 mb-4">
                Hambre shall not be liable for:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600 mb-4">
                <li>Any accidents, injuries, or damages occurring during delivery activities</li>
                <li>Any traffic violations or fines incurred</li>
                <li>Loss of earnings due to platform unavailability</li>
                <li>Disputes between contractors and customers or restaurants</li>
                <li>Any indirect, incidental, or consequential damages</li>
              </ul>

              <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-4">8. Termination</h2>
              <p className="text-slate-600 mb-4">
                Either party may terminate this agreement at any time. Upon termination:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-slate-600 mb-4">
                <li>You will receive payment for any completed work not yet paid</li>
                <li>Your access to the platform will be revoked</li>
                <li>Any ongoing commission arrangements will be reviewed on a case-by-case basis</li>
              </ul>

              <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-4">9. Data Protection</h2>
              <p className="text-slate-600 mb-4">
                By using the Hambre platform, you consent to the collection and processing of your personal data in 
                accordance with our <a href="/privacy" className="text-orange-500 hover:underline">Privacy Policy</a> and 
                applicable data protection laws including the UK GDPR.
              </p>
              <p className="text-slate-600 mb-4">
                For specific information about how we handle driver verification documents, please refer to 
                our <a href="/driver-document-policy" className="text-orange-500 hover:underline">Driver Document Handling Policy</a>.
              </p>

              <h2 className="text-lg font-semibold text-slate-800 mt-8 mb-4">10. Governing Law</h2>
              <p className="text-slate-600 mb-4">
                This Agreement shall be governed by and construed in accordance with the laws of England and Wales. 
                Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>

              <div className="border-t border-slate-200 mt-8 pt-6">
                <p className="text-sm text-slate-500 mt-2">
                  By checking the agreement checkbox during registration, you confirm that you have read, understood, 
                  and agree to be bound by these terms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContractorTermsPage;
